import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import {
  SCORECARD_LINK_HEADER,
  type CockpitView,
  type CorrectScorecardInput,
  type DraftSavedView,
  type FlagInterviewInput,
  type InterviewFlagKind,
  type InterviewFlagView,
  type InterviewScorecardsView,
  type SaveScorecardDraftInput,
  type ScorecardLinkView,
  type SubmitScorecardInput,
  type SubmitScorecardLinkInput,
  type WaiveScorecardInput,
} from '@nest-recruit/shared';
import { ApiError, apiDelete, apiGet, apiPost, apiPut, apiUrl, request } from '../../../lib/api-client';

// --- C6: INT-04, the panelist's scorecard by link (ADR 0077) ---------------------------------

export function scorecardLinkKey(token: string): readonly unknown[] {
  return ['scorecard-link', token];
}

/** The token goes in a header, never the address the API sees, so it lands in no access log. */
function linkHeaders(token: string): Readonly<Record<string, string>> {
  return { [SCORECARD_LINK_HEADER]: token };
}

export function useScorecardLink(token: string): UseQueryResult<ScorecardLinkView, ApiError> {
  return useQuery({
    queryKey: scorecardLinkKey(token),
    queryFn: async () => (await request<ScorecardLinkView>('/scorecard-link', { headers: linkHeaders(token) })).data,
    enabled: token !== '',
    // A closed link stays closed; only a dropped connection is worth trying again.
    retry: (count, error) => error.status === 0 && count < 3,
  });
}

export const scorecardLinkApi = {
  submit: async (token: string, input: SubmitScorecardLinkInput) => (await request<{ readonly isSubmitted: true }>('/scorecard-link/submit', { method: 'POST', body: input, headers: linkHeaders(token) })).data,
  /** The CV, fetched with the token in its header and opened from memory in a new tab. */
  openCv: async (token: string): Promise<void> => {
    // The tab opens on the tap itself, so a phone's pop-up blocker lets it through; the file follows.
    const tab = window.open('', '_blank');
    if (!tab) throw new ApiError({ code: 'popup_blocked', message: 'Allow pop-ups for this page to open the CV.', status: 0 });
    tab.opener = null;
    const response = await fetch(apiUrl('/scorecard-link/cv'), { headers: linkHeaders(token), credentials: 'omit' });
    if (!response.ok) {
      tab.close();
      throw new ApiError({ code: 'not_found', message: 'The CV could not be opened.', status: response.status });
    }
    tab.location.href = URL.createObjectURL(await response.blob());
  },
};

/**
 * S20 — INT-01…03. The server's realtime hints name `cockpit` and `scorecards`, so a
 * peer's submission unlocks the round on screens already open, without a reload.
 */

export const COCKPIT_KEY = ['cockpit'] as const;
export const SCORECARDS_KEY = ['scorecards'] as const;

export function cockpitKey(interviewId: string): readonly unknown[] {
  return [...COCKPIT_KEY, interviewId];
}

export function interviewScorecardsKey(interviewId: string): readonly unknown[] {
  return [...SCORECARDS_KEY, interviewId];
}

export function useCockpit(interviewId: string): UseQueryResult<CockpitView, ApiError> {
  return useQuery({
    queryKey: cockpitKey(interviewId),
    queryFn: () => apiGet<CockpitView>(`/interviews/${interviewId}/cockpit`),
    // A panelist on flaky wifi must never see the cockpit drop to a skeleton on a refetch (§5a rule 2).
    placeholderData: (previous) => previous,
    retry: (count, error) => error.status >= 500 && count < 2,
  });
}

export function useInterviewScorecards(interviewId: string): UseQueryResult<InterviewScorecardsView, ApiError> {
  return useQuery({
    queryKey: interviewScorecardsKey(interviewId),
    queryFn: () => apiGet<InterviewScorecardsView>(`/interviews/${interviewId}/scorecards`),
    placeholderData: (previous) => previous,
  });
}

/** Statuses that mean the request never reached the app: no connection, or the proxy in front of it. */
const UNREACHED_STATUSES: ReadonlySet<number> = new Set([0, 502, 503, 504]);
const OPENED_RETRY_DELAYS_MS: readonly number[] = [1_000, 4_000];

function neverReachedApp(error: unknown): boolean {
  return error instanceof ApiError && UNREACHED_STATUSES.has(error.status);
}

function pause(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

/**
 * F11.01 / F11.02. Bug #40: the marker went out once, and a 503 from the proxy in front of
 * the app lost the open for good. A proxy never repeats a POST the way it does a GET, so
 * this does — only for failures that never reached the app, so a refusal is not resent.
 */
async function markOpened(scorecardId: string): Promise<void> {
  for (let attempt = 0; ; attempt += 1) {
    try {
      await apiPost<null>(`/scorecards/${scorecardId}/opened`);
      return;
    } catch (error) {
      const wait = OPENED_RETRY_DELAYS_MS[attempt];
      if (wait === undefined || !neverReachedApp(error)) throw error;
      await pause(wait);
    }
  }
}

export const scorecardsApi = {
  opened: markOpened,
  saveDraft: (scorecardId: string, input: SaveScorecardDraftInput) => apiPut<DraftSavedView>(`/scorecards/${scorecardId}/draft`, input),
  submit: (scorecardId: string, input: SubmitScorecardInput) => apiPost<CockpitView>(`/scorecards/${scorecardId}/submit`, input),
  correct: (scorecardId: string, input: CorrectScorecardInput) => apiPost<InterviewScorecardsView>(`/scorecards/${scorecardId}/correct`, input),
  waive: (scorecardId: string, input: WaiveScorecardInput) => apiPost<InterviewScorecardsView>(`/scorecards/${scorecardId}/waive`, input),
  flag: (interviewId: string, input: FlagInterviewInput) => apiPost<readonly InterviewFlagView[]>(`/interviews/${interviewId}/flags`, input),
  unflag: (interviewId: string, kind: InterviewFlagKind) => apiDelete<readonly InterviewFlagView[]>(`/interviews/${interviewId}/flags/${kind}`),
};
